import { useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import { api } from "@/api/client";
import type { RollupPeriodType, RollupSchedule } from "../../../types";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { cn } from "@/lib/utils";

const PERIODS: { type: RollupPeriodType; label: string; hint: string }[] = [
  { type: "month", label: "Monthly", hint: "generates on the 1st for the previous month" },
  { type: "quarter", label: "Quarterly", hint: "generates at each quarter start for the previous quarter" },
  { type: "year", label: "Yearly", hint: "generates on Jan 1 for the previous year" },
];

interface AutoRollupCardProps {
  /** Called after a toggle, so the parent can refetch the list (an enable may generate a rollup within a minute). */
  onChanged: () => void;
}

export function AutoRollupCard({ onChanged }: AutoRollupCardProps) {
  const [schedules, setSchedules] = useState<RollupSchedule[]>([]);
  const [pending, setPending] = useState<RollupPeriodType | null>(null);

  useEffect(() => {
    api.rollups.autoSchedules().then(setSchedules).catch(() => {});
  }, []);

  const isEnabled = (type: RollupPeriodType) => schedules.find(s => s.period_type === type)?.enabled ?? false;

  const handleToggle = async (type: RollupPeriodType) => {
    setPending(type);
    try {
      const updated = await api.rollups.setAutoSchedule(type, !isEnabled(type));
      setSchedules(prev => {
        const rest = prev.filter(s => s.period_type !== type);
        return [...rest, updated];
      });
      onChanged();
    } catch {
      // toggle stays as-is; next fetch corrects it
    } finally {
      setPending(null);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <RefreshCw className="w-4 h-4 text-brand-600" />
          Automatic Rollups
        </CardTitle>
        <CardDescription>
          Generate recaps on a cadence — enabling one also creates the most recently finished period right away.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid gap-3 sm:grid-cols-3">
          {PERIODS.map(period => {
            const enabled = isEnabled(period.type);
            return (
              <button
                key={period.type}
                onClick={() => handleToggle(period.type)}
                disabled={pending !== null}
                className={cn(
                  "text-left rounded-lg border p-3.5 transition-colors cursor-pointer",
                  enabled ? "border-brand-600 bg-brand-50" : "border-slate-200 bg-white hover:border-slate-300",
                )}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className={cn("text-[13px] font-semibold", enabled ? "text-brand-950" : "text-slate-600")}>
                    {period.label}
                  </span>
                  <span
                    className={cn(
                      "w-8 h-4.5 rounded-full relative transition-colors shrink-0",
                      enabled ? "bg-brand-600" : "bg-slate-200",
                    )}
                  >
                    <span
                      className={cn(
                        "absolute top-0.5 w-3.5 h-3.5 rounded-full bg-white transition-all",
                        enabled ? "left-4" : "left-0.5",
                      )}
                    />
                  </span>
                </div>
                <p className="text-[11px] text-slate-400 leading-snug">{period.hint}</p>
              </button>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
